import { CalendarChevronLeftSVG, CalendarChevronRightSVG } from "@/assets/svg";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onChange: (dates: { startDate: string; endDate: string }) => void;
  placeholder?: string;
  readOnly?: boolean;
  minDate?: string; // YYYY-MM-DD format
  maxDate?: string; // YYYY-MM-DD format
  containerClassName?: string;
  showAbove?: boolean; // Force the calendar to show above the input
  // doneButtonText?: string; // Text for the done button
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  placeholder = "Select date range",
  readOnly = false,
  minDate,
  maxDate,
  containerClassName = "",
  showAbove = false,
  // doneButtonText = "Apply",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(
    startDate ? new Date(startDate) : new Date()
  );
  const [nextMonth, setNextMonth] = useState<Date>(
    new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
  );
  const [showYearSelector, setShowYearSelector] = useState(false);
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [shouldShowAbove, setShouldShowAbove] = useState(showAbove);
  const [calendarPositionStyle, setCalendarPositionStyle] = useState({});
  const [selectionMode, setSelectionMode] = useState<"start" | "end">("start");
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const [tempStartDate, setTempStartDate] = useState<string>(startDate);
  const [tempEndDate, setTempEndDate] = useState<string>(endDate);

  const datePickerRef = useRef<HTMLDivElement>(null);
  const yearSelectorRef = useRef<HTMLDivElement>(null);
  const monthSelectorRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Month names
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Generate a range of years (from current year - 100 to current year + 20)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 121 }, (_, i) => currentYear - 100 + i);

  // Determine if calendar should show above based on position in viewport
  const checkPosition = useCallback(() => {
    if (!datePickerRef.current) return;

    const inputRect = datePickerRef.current.getBoundingClientRect();
    const calendarHeight = 400; // Approximate height of the calendar
    const calendarWidth = 648; // Approximate width of the calendar

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;
    const spaceRight = viewportWidth - inputRect.left;
    const spaceLeft = inputRect.right;

    let top = inputRect.height + 5;
    let left: number | string = 0;
    let right: number | string = 'auto';

    // Vertical positioning
    if (spaceBelow < calendarHeight && spaceAbove > calendarHeight) {
      setShouldShowAbove(true);
      top = -calendarHeight - 5;
    } else {
      setShouldShowAbove(false);
    }

    // Horizontal positioning
    if (spaceRight < calendarWidth && spaceLeft > calendarWidth) {
      // Not enough space on the right, show on the left
      left = 'auto';
      right = 0;
    } else {
      // Default to show on the right
      left = 0;
      right = 'auto';
    }

    setCalendarPositionStyle({ top: `${top}px`, left, right });

  }, [showAbove]);

  // Check position when opening the calendar
  useEffect(() => {
    if (isOpen) {
      checkPosition();
    }
  }, [isOpen, checkPosition]);

  // Update next month when current month changes
  useEffect(() => {
    setNextMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  }, [currentMonth]);

  // Update temporary dates when props change or when the dropdown opens
  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);

    // Reset to current month when opening the picker with no dates selected
    if (isOpen && !startDate && !endDate) {
      const today = new Date();
      setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    }

    if (isOpen && startDate) {
      setCurrentMonth(new Date(startDate));
    }
  }, [startDate, endDate, isOpen]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowYearSelector(false);
        setShowMonthSelector(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Format date as YYYY-MM-DD for input value
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Format date as MM/DD/YYYY for display
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${month}/${day}/${year}`;
  };

  // Format date range for display
  const formatDateRangeForDisplay = (): string => {
    if (!startDate && !endDate) return placeholder;
    if (startDate && !endDate)
      return `${formatDateForDisplay(startDate)} - End date`;
    if (!startDate && endDate)
      return `Start date - ${formatDateForDisplay(endDate)}`;
    return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(
      endDate
    )}`;
  };

  // Get days in month
  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get day of week (0-6) for first day of month
  const getFirstDayOfMonth = (year: number, month: number): number => {
    return new Date(year, month, 1).getDay();
  };

  // Generate calendar days for a specific month
  const generateCalendarDays = (year: number, month: number) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);

    // Get days from previous month
    const daysInPrevMonth = getDaysInMonth(year, month - 1);
    const prevMonthDays = Array.from({ length: firstDayOfMonth }, (_, i) => ({
      day: daysInPrevMonth - firstDayOfMonth + i + 1,
      month: month - 1,
      year: month === 0 ? year - 1 : year,
      isCurrentMonth: false,
    }));

    // Current month days
    const currentMonthDays = Array.from({ length: daysInMonth }, (_, i) => ({
      day: i + 1,
      month,
      year,
      isCurrentMonth: true,
    }));

    // Calculate how many days we need from next month
    const totalDaysShown = 35; // 5 rows of 7 days
    const nextMonthDays = Array.from(
      {
        length:
          totalDaysShown - (prevMonthDays.length + currentMonthDays.length),
      },
      (_, i) => ({
        day: i + 1,
        month: month + 1,
        year: month === 11 ? year + 1 : year,
        isCurrentMonth: false,
      })
    );

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
    setNextMonth(
      new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 1)
    );
  };

  // Go to today
  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  // Handle month change from selector
  const handleMonthChange = (monthIndex: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex, 1));
    setShowMonthSelector(false);
  };

  // Handle year change from selector
  const handleYearChange = (year: number) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
    setShowYearSelector(false);
  };

  // Check if a date is within the allowed range
  const isDateInRange = (year: number, month: number, day: number): boolean => {
    const date = new Date(year, month, day);
    date.setHours(0, 0, 0, 0);

    let isAfterMinDate = true;
    let isBeforeMaxDate = true;

    if (minDate) {
      const minDateObj = new Date(minDate);
      minDateObj.setHours(0, 0, 0, 0);
      isAfterMinDate = date >= minDateObj;
    }

    if (maxDate) {
      const maxDateObj = new Date(maxDate);
      maxDateObj.setHours(0, 0, 0, 0);
      isBeforeMaxDate = date <= maxDateObj;
    }

    return isAfterMinDate && isBeforeMaxDate;
  };

  // Check if a date is selected
  const isDateSelected = (
    year: number,
    month: number,
    day: number
  ): boolean => {
    const dateStr = formatDateForInput(new Date(year, month, day));
    return dateStr === tempStartDate || dateStr === tempEndDate;
  };

  // Check if a date is in the selected range
  const isDateInSelectedRange = (
    year: number,
    month: number,
    day: number
  ): boolean => {
    if (!tempStartDate || !tempEndDate) return false;

    const dateStr = formatDateForInput(new Date(year, month, day));
    const currentDate = new Date(dateStr);
    const start = new Date(tempStartDate);
    const end = new Date(tempEndDate);

    return currentDate > start && currentDate < end;
  };

  // Check if a date is in the hovered range (when selecting end date)
  const isDateInHoveredRange = (
    year: number,
    month: number,
    day: number
  ): boolean => {
    if (!tempStartDate || !hoveredDate || selectionMode !== "end") return false;

    const dateStr = formatDateForInput(new Date(year, month, day));
    const currentDate = new Date(dateStr);
    const start = new Date(tempStartDate);
    const hovered = new Date(hoveredDate);

    if (hovered < start) {
      return currentDate < start && currentDate > hovered;
    } else {
      return currentDate > start && currentDate < hovered;
    }
  };

  // Handle date selection
  const handleDateSelect = (year: number, month: number, day: number) => {
    if (!isDateInRange(year, month, day)) return;

    const selectedDate = formatDateForInput(new Date(year, month, day));

    if (selectionMode === "start") {
      // If selecting start date
      setTempStartDate(selectedDate);
      setTempEndDate("");
      setSelectionMode("end");
    } else {
      // If selecting end date
      const startDateObj = new Date(tempStartDate);
      const selectedDateObj = new Date(selectedDate);

      // Ensure end date is after start date
      if (selectedDateObj >= startDateObj) {
        setTempEndDate(selectedDate);
        onChange({ startDate: tempStartDate, endDate: selectedDate });
      } else {
        // If selected date is before start date, swap them
        setTempEndDate(tempStartDate);
        setTempStartDate(selectedDate);
        onChange({ startDate: selectedDate, endDate: tempEndDate });
      }

      setIsOpen(false);
      setSelectionMode("start");
    }
  };

  // Handle mouse hover over a date (for range preview)
  const handleDateHover = (year: number, month: number, day: number) => {
    if (selectionMode === "end" && tempStartDate) {
      setHoveredDate(formatDateForInput(new Date(year, month, day)));
    }
  };

  // // Check if Apply button should be disabled
  // const isApplyButtonDisabled = (): boolean => {
  //   // Disable if no dates are selected
  //   if (!tempStartDate && !tempEndDate) return true;

  //   // Disable if values haven't changed
  //   if (tempStartDate === startDate && tempEndDate === endDate) return true;

  //   return false;
  // };

  // // Handle done button click
  // const handleDone = () => {
  //   // Don't do anything if the button is disabled
  //   if (isApplyButtonDisabled()) return;

  //   // Trigger onChange with the temporary dates
  //   onChange({ startDate: tempStartDate, endDate: tempEndDate });

  //   // Close the calendar
  //   setIsOpen(false);
  // };

  return (
    <div className={`${containerClassName}`}>
      <div className="relative sm:static" ref={datePickerRef}>
        <div
          className={`w-full h-[42px] px-3 bg-[#F9FBFF] rounded-[6px] flex items-center justify-between cursor-pointer`}
          onClick={() => !readOnly && setIsOpen(!isOpen)}
        >
          {/* ${
              startDate || endDate ? "" : "text-[#B3B3B3]"
            } */}
          <span className={`text-[#1E1E1E] font-[400] text-sm `}>
            {formatDateRangeForDisplay()}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </div>

        {isOpen && (
          <div
            ref={calendarRef}
            className={`absolute z-50 min-w-[648px] bg-white border border-[#CCCCCC] shadow-lg rounded-[6px] ${
              shouldShowAbove ? "bottom-[44px]" : "mt-1"
            }`}
            style={calendarPositionStyle}
          >
            <div className="p-4 flex flex-col gap-4">
              <div className="flex justify-between">
                {/* Left calendar (current month) */}
                <div className="w-1/2 pr-2">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-1">
                      {/* Month selector */}
                      <div className="relative">
                        <button
                          type="button"
                          className="font-bold text-black text-xs hover:bg-gray-100 px-1 py-0.5 rounded"
                          onClick={() => {
                            setShowMonthSelector(!showMonthSelector);
                            setShowYearSelector(false);
                          }}
                        >
                          {monthNames[currentMonth.getMonth()]}
                        </button>
                        {showMonthSelector && (
                          <div
                            ref={monthSelectorRef}
                            className="absolute z-50 mt-1 bg-white border border-gray-200 shadow-lg rounded max-h-48 overflow-y-auto w-32"
                          >
                            {monthNames.map((month, index) => (
                              <button
                                key={month}
                                type="button"
                                className={`w-full text-left px-2 py-1 text-xs month-${index} ${
                                  index === currentMonth.getMonth()
                                    ? "bg-[#73B5C5] text-white"
                                    : "hover:bg-gray-100"
                                }`}
                                onClick={() => handleMonthChange(index)}
                              >
                                {month}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Year selector */}
                      <div className="relative">
                        <button
                          type="button"
                          className="font-bold text-black text-xs hover:bg-gray-100 px-1 py-0.5 rounded"
                          onClick={() => {
                            setShowYearSelector(!showYearSelector);
                            setShowMonthSelector(false);
                          }}
                        >
                          {currentMonth.getFullYear()}
                        </button>
                        {showYearSelector && (
                          <div
                            ref={yearSelectorRef}
                            className="absolute z-50 mt-1 bg-white border border-gray-200 shadow-lg rounded max-h-48 overflow-y-auto w-20"
                          >
                            {years.map((year) => (
                              <button
                                key={year}
                                type="button"
                                className={`w-full text-left px-2 py-1 text-xs year-${year} ${
                                  year === currentMonth.getFullYear()
                                    ? "bg-[#73B5C5] text-white"
                                    : "hover:bg-gray-100"
                                }`}
                                onClick={() => handleYearChange(year)}
                              >
                                {year}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="grid grid-cols-7 gap-1 text-center text-[16px] font-[400] text-[#737373]">
                      <div>S</div>
                      <div>M</div>
                      <div>T</div>
                      <div>W</div>
                      <div>T</div>
                      <div>F</div>
                      <div>S</div>
                    </div>

                    <div className="grid grid-cols-7 gap-y-1 mt-1">
                      {generateCalendarDays(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth()
                      ).map((dateObj, index) => (
                        <button
                          key={`left-${index}`}
                          type="button"
                          className={`
                            h-8 w-full text-[16px] font-[400] flex items-center justify-center
                            ${
                              !dateObj.isCurrentMonth
                                ? "text-gray-400 cursor-not-allowed opacity-50"
                                : !isDateInRange(
                                    dateObj.year,
                                    dateObj.month,
                                    dateObj.day
                                  )
                                ? "text-gray-400 cursor-not-allowed"
                                : isDateSelected(
                                    dateObj.year,
                                    dateObj.month,
                                    dateObj.day
                                  )
                                ? "bg-[#73B5C5] text-white cursor-pointer"
                                : isDateInSelectedRange(
                                    dateObj.year,
                                    dateObj.month,
                                    dateObj.day
                                  )
                                ? "bg-[#BCDFF2] text-black cursor-pointer"
                                : isDateInHoveredRange(
                                    dateObj.year,
                                    dateObj.month,
                                    dateObj.day
                                  )
                                ? "bg-[#F0F9FB] cursor-pointer"
                                : "hover:bg-gray-100 text-[#444444] cursor-pointer"
                            }
                          `}
                          onClick={() =>
                            isDateInRange(
                              dateObj.year,
                              dateObj.month,
                              dateObj.day
                            ) &&
                            handleDateSelect(
                              dateObj.year,
                              dateObj.month,
                              dateObj.day
                            )
                          }
                          onMouseEnter={() =>
                            handleDateHover(
                              dateObj.year,
                              dateObj.month,
                              dateObj.day
                            )
                          }
                        >
                          {dateObj.day}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right calendar (next month) */}
                <div className="w-1/2 pl-3 border-l border-[#CCCCCC]">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-1">
                      <span className="font-bold text-black text-xs">
                        {monthNames[nextMonth.getMonth()]}{" "}
                        {nextMonth.getFullYear()}
                      </span>
                    </div>

                    <div className="flex items-center">
                      <button
                        type="button"
                        className="cursor-pointer"
                        onClick={goToPreviousMonth}
                      >
                        <CalendarChevronLeftSVG />
                      </button>

                      <button
                        type="button"
                        className="cursor-pointer"
                        onClick={goToNextMonth}
                      >
                        <CalendarChevronRightSVG />
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="grid grid-cols-7 gap-1 text-center text-[16px] font-[400] text-[#737373]">
                      <div>S</div>
                      <div>M</div>
                      <div>T</div>
                      <div>W</div>
                      <div>T</div>
                      <div>F</div>
                      <div>S</div>
                    </div>

                    <div className="grid grid-cols-7 gap-y-1 mt-1">
                      {generateCalendarDays(
                        nextMonth.getFullYear(),
                        nextMonth.getMonth()
                      ).map((dateObj, index) => (
                        <button
                          key={`right-${index}`}
                          type="button"
                          className={`
                            h-8 w-full text-[16px] font-[400] flex items-center justify-center
                            ${
                              !dateObj.isCurrentMonth
                                ? "text-gray-400 cursor-not-allowed opacity-50"
                                : !isDateInRange(
                                    dateObj.year,
                                    dateObj.month,
                                    dateObj.day
                                  )
                                ? "text-gray-400 cursor-not-allowed"
                                : isDateSelected(
                                    dateObj.year,
                                    dateObj.month,
                                    dateObj.day
                                  )
                                ? "bg-[#73B5C5] text-white cursor-pointer"
                                : isDateInSelectedRange(
                                    dateObj.year,
                                    dateObj.month,
                                    dateObj.day
                                  )
                                ? "bg-[#BCDFF2] text-black cursor-pointer"
                                : isDateInHoveredRange(
                                    dateObj.year,
                                    dateObj.month,
                                    dateObj.day
                                  )
                                ? "bg-[#F0F9FB] cursor-pointer"
                                : "hover:bg-gray-100 text-[#444444] cursor-pointer"
                            }
                          `}
                          onClick={() =>
                            isDateInRange(
                              dateObj.year,
                              dateObj.month,
                              dateObj.day
                            ) &&
                            handleDateSelect(
                              dateObj.year,
                              dateObj.month,
                              dateObj.day
                            )
                          }
                          onMouseEnter={() =>
                            handleDateHover(
                              dateObj.year,
                              dateObj.month,
                              dateObj.day
                            )
                          }
                        >
                          {dateObj.day}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Done button */}
              {/* <div className="flex justify-end">
                <button
                  type="button"
                  className={`text-sm font-medium hover:underline ${
                    isApplyButtonDisabled()
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-[#73B5C5] cursor-pointer"
                  }`}
                  onClick={handleDone}
                  disabled={isApplyButtonDisabled()}
                >
                  {doneButtonText}
                </button>
              </div> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangePicker;
